import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { useFamilyStripePaymentMethodsQuery } from '~/pages/FamilyDetail/useFamilyStripePaymentMethodsQuery';
import { useSetDefaultPaymentMethod } from '~/pages/FamilyDetail/useSetDefaultPaymentMethod';
import { AddPaymentMethodButton } from './AddPaymentMethodButton';
import { describePaymentMethod } from './describePaymentMethod';

interface ParentPaymentMethodsPanelProps {
  familyEnrollmentId: string;
  onChanged?: () => void;
}

// The saved methods a parent chooses from when paying an invoice themselves.
// One of them is the auto-pay method; any other can be made so. Subscription
// overrides are an admin tool while subscriptions last, so none are offered
// here.
export function ParentPaymentMethodsPanel({ familyEnrollmentId, onChanged }: ParentPaymentMethodsPanelProps) {
  const { data, loading, error, refetch } = useFamilyStripePaymentMethodsQuery(familyEnrollmentId, { skip: false });
  const [setDefaultPaymentMethod, { loading: setDefaultLoading }] = useSetDefaultPaymentMethod();
  const [settingDefaultPmId, setSettingDefaultPmId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const paymentMethods = data?.familyStripePaymentMethods ?? [];

  const handleUseForAutoPay = async (paymentMethodId: string) => {
    setActionError(null);
    setSettingDefaultPmId(paymentMethodId);
    try {
      await setDefaultPaymentMethod({ variables: { familyEnrollmentId, paymentMethodId } });
      await refetch();
      onChanged?.();
    } catch {
      setActionError('Could not change your auto-pay method. Please try again.');
    } finally {
      setSettingDefaultPmId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-3">
          <span>Saved payment methods</span>
          <Button variant="outline" size="sm" disabled={loading} onClick={() => { setActionError(null); void refetch(); }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
            We couldn&apos;t load your payment methods right now. Please try again.
          </div>
        )}
        {actionError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive">
            {actionError}
          </div>
        )}
        {loading && !data ? (
          <p className="text-muted-foreground">Loading your saved payment methods…</p>
        ) : !error && paymentMethods.length === 0 ? (
          <p className="text-muted-foreground">You don&apos;t have any payment methods on file yet.</p>
        ) : (
          <ul className="space-y-2">
            {paymentMethods.map((pm) => (
              <li key={pm.paymentMethodId} className="flex items-center justify-between gap-3">
                <span>{describePaymentMethod(pm)}</span>
                {pm.isDefault ? (
                  <Badge variant="default">Auto-pay</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={setDefaultLoading}
                    onClick={() => { void handleUseForAutoPay(pm.paymentMethodId); }}
                  >
                    {settingDefaultPmId === pm.paymentMethodId
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : 'Use for auto-pay'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        <AddPaymentMethodButton familyEnrollmentId={familyEnrollmentId} variant="outline" size="sm" className="pt-1" />
      </CardContent>
    </Card>
  );
}
